import React, { useEffect, useState, useMemo } from 'react'
import styled from 'styled-components'
import { GlobalTable, HeaderLine, Td, Tr } from '../../../../components/styles/table-style'
import { useOutletContext } from 'react-router-dom'
import { Filter } from '../../../../components/SubHeaderFilter/types'
import { FashionOutletType } from '..'
import axios from '../../../../config/axios'
import { LoadMoreProvider, useLoadMore } from '../../../../contexts/LoadMoreContext'
import LoadMoreButton from '../../../../components/LoadMoreButton'
import ActionsDropdown from './ActionsDropdown'
import eventBus from '../../../../utils/eventBus'
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender, ColumnDef, SortingState } from '@tanstack/react-table'
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react'

const ORDERS_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders`

const OrdersTableContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  width: 100%;
`

const TableScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
`

const SaleStatusBadge = styled.span<{ $variant: 'closed' | 'open' | 'cancelled' }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  ${props => props.$variant === 'closed' ? `
    background-color: #E9FBEF;
    color: #16A34A;
    border: 1px solid #A7E2BA;
  ` : props.$variant === 'cancelled' ? `
    background-color: #FDECEC;
    color: #DC2626;
    border: 1px solid #F3B4B4;
  ` : `
    background-color: #F8F9FA;
    color: #6B7280;
    border: 1px solid #E5E7EB;
  `}
`

const ThContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

interface ThProps {
  $sortable?: boolean
}

const Th = styled.th<ThProps>`
  color: #696969;
  background-color: #f5f5f5;
  text-align: start;
  border: 0;
  font-size: .85rem;
  font-weight: 400;
  padding-left: 1.2rem;
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  transition: background 0.2s;
  &:hover {
    background: ${({ $sortable }) => ($sortable ? '#f1f3f5' : '#f5f5f5')};
  }
`

type OrderType = {
  id: number
  clientId: number
  client: string | null
  createdAt: string
  startDateOfDelivery: string
  orderDate: string
  quantityItems: string
  quantityPieces: string
  status: boolean
  totalCost: number
  total: number
  saleStatus: string
}

const FashionOrdersTableContent: React.FC = () => {
  const { setTabIndex, search, appliedFilters } = useOutletContext<FashionOutletType>()
  const [orders, setOrders] = useState<OrderType[]>([])
  const [currentCount, setCurrentCount] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const { hasNextPage, setHasNextPage, isFetchingNextPage, setIsFetchingNextPage, setFetchNextPageFunction } = useLoadMore()
  const [debouncedSearch, setDebouncedSearch] = useState(search || '')
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'id', desc: true }
  ])

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search || '')
    }, 400)

    return () => {
      clearTimeout(handler)
    }
  }, [search])

  const fetchData = async (page: number, append: boolean = false) => {
    try {
      if (append) setIsFetchingNextPage(true)

      const size = 50
      const pageOneBased = page + 1
      const response = await axios.get(`${ORDERS_URL}/order/paginated`, {
        params: {
          page: pageOneBased,
          limit: size,
          onlyActive: 'true',
          search: debouncedSearch || undefined,
          filters: appliedFilters && appliedFilters.length > 0 ? JSON.stringify(appliedFilters) : undefined
        }
      })

      const result = response.data
      const ordersData = result.data || []
      const total = result.total || 0

      if (append) {
        setOrders(prev => [...prev, ...ordersData])
        setCurrentCount((prev: number) => prev + ordersData.length)
      } else {
        setOrders(ordersData)
        setCurrentCount(ordersData.length)
        setTotalCount(total)
      }

      const newCurrent = append ? currentCount + ordersData.length : ordersData.length
      setHasNextPage(newCurrent < total)
    } catch (error) {
      console.error('Erro ao buscar os pedidos:', error)
    } finally {
      if (append) setIsFetchingNextPage(false)
    }
  }

  useEffect(() => {
    fetchData(0)
  }, [debouncedSearch, appliedFilters])

  useEffect(() => {
    const handler = () => fetchData(0)
    eventBus.on('refreshFashionTable', handler)
    return () => eventBus.off('refreshFashionTable', handler)
  }, [debouncedSearch, appliedFilters])

  useEffect(() => {
    setFetchNextPageFunction(() => fetchData(Math.floor(currentCount / 50), true))
  }, [currentCount, debouncedSearch, appliedFilters])

  const filteredOrders = useMemo(() => {
    return orders
  }, [orders])

  const hasMore = currentCount < totalCount

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'N/A'
    try {
      const d = new Date(dateStr)
      return d.toLocaleDateString('pt-BR')
    } catch {
      return 'N/A'
    }
  }

  const getSaleStatusVariant = (saleStatus: string): 'closed' | 'open' | 'cancelled' => {
    if (saleStatus === 'Fechado') return 'closed'
    if (saleStatus === 'Cancelado') return 'cancelled'
    return 'open'
  }

  const columns = useMemo<ColumnDef<OrderType>[]>(() => [
    {
      accessorKey: 'id',
      header: 'Nº do pedido',
    },
    {
      accessorKey: 'client',
      header: 'Cliente',
      cell: (info) => info.getValue() || 'N/A',
    },
    {
      accessorKey: 'orderDate',
      header: 'Data pedido',
      cell: (info) => {
        const row = info.row.original
        return formatDate(info.getValue() as string || row.createdAt)
      },
    },
    {
      accessorKey: 'startDateOfDelivery',
      header: 'Data entrega',
      cell: (info) => formatDate(info.getValue() as string),
    },
    {
      accessorKey: 'quantityItems',
      header: 'Qtd. de itens',
      cell: (info) => info.getValue() || '0',
    },
    {
      accessorKey: 'quantityPieces',
      header: 'Qtd. de peças',
      cell: (info) => info.getValue() || '0',
    },

    {
      id: 'total',
      header: 'Total (R$)',
      accessorFn: (row) => Number(row.totalCost) || Number(row.total) || 0,
      cell: (info) => {
        const val = info.getValue() as number
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val)
      },
    },
    {
      accessorKey: 'saleStatus',
      header: 'Venda',
      cell: (info) => {
        const val = info.getValue() as string
        return (
          <SaleStatusBadge $variant={getSaleStatusVariant(val)}>
            {val || '-'}
          </SaleStatusBadge>
        )
      },
    },
    {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <ActionsDropdown
          order={row.original}
          onDelete={() => setOrders((prev: OrderType[]) => prev.filter((o) => o.id !== row.original.id))}
          onUpdate={() => fetchData(0)}
        />
      ),
    },
  ], [])

  const table = useReactTable({
    data: filteredOrders,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <OrdersTableContainer>
      <TableScroll>
        <GlobalTable>
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <HeaderLine key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <Th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    $sortable={header.column.getCanSort()}
                  >
                    <ThContent>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        {
                          asc: <ArrowUp size={14} />,
                          desc: <ArrowDown size={14} />,
                        }[header.column.getIsSorted() as string] ?? <ArrowUpDown size={14} style={{ opacity: 0.4 }} />
                      )}
                    </ThContent>
                  </Th>
                ))}
              </HeaderLine>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <Tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <Td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </tbody>
        </GlobalTable>

        <LoadMoreButton
          currentCount={currentCount}
          totalCount={totalCount}
          hasMore={hasMore}
          onLoadMore={() => fetchData(Math.floor(currentCount / 50), true)}
          isLoading={isFetchingNextPage}
        />
      </TableScroll>
    </OrdersTableContainer>
  )
}

const FashionOrdersTable: React.FC = () => {
  return (
    <LoadMoreProvider>
      <FashionOrdersTableContent />
    </LoadMoreProvider>
  )
}

export default FashionOrdersTable
